import React, { useCallback, useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { api, loadBuildingsForSelector } from '../../../api';
import { useAuth } from '../../../contexts/AuthContext';
import {applyDefaultBuildingSelection, needsBuildingSelection, shouldShowBuildingSelector, getBuildingSelectProps, selectBuilding } from '../../../utils/buildingSelection';
import { IeImportBatch, IeImportType, incomeExpenseApi } from '../../../api/incomeExpenseApi';
import ImportFileLink from '../../../components/common/ImportFileLink';

interface DataImportTabProps {
  importType: IeImportType;
  title: string;
}

const importApiMap: Record<IeImportType, (buildingId: string, file: File) => Promise<any>> = {
  REVENUE: incomeExpenseApi.importRevenue,
  EXPENDITURE: incomeExpenseApi.importExpenditure,
  REVENUE_PLAN: incomeExpenseApi.importRevenuePlan,
};

export const DataImportTab: React.FC<DataImportTabProps> = ({ importType, title }) => {
  const { user } = useAuth();
  const [buildings, setBuildings] = useState<Array<{ id: string; name: string }>>([]);
  const [buildingId, setBuildingId] = useState('');
  const [batches, setBatches] = useState<IeImportBatch[]>([]);
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [importFile, setImportFile] = useState<File | null>(null);
  const [importing, setImporting] = useState(false);
  const [importError, setImportError] = useState<string | null>(null);

  useEffect(() => {
    loadBuildingsForSelector().then((data) => {
      const list = data || [];
      setBuildings(list);
      setBuildingId((prev) => applyDefaultBuildingSelection(list, user, prev));
    });
  }, [user]);

  const loadBatches = useCallback(async () => {
    if (!buildingId) return;
    setLoading(true);
    try {
      setBatches(await incomeExpenseApi.listImportBatches(buildingId, importType));
    } catch {
      toast.error('Không thể tải lịch sử import');
      setBatches([]);
    } finally {
      setLoading(false);
    }
  }, [buildingId, importType]);

  useEffect(() => {
    loadBatches();
  }, [loadBatches]);

  const handleDownloadSample = async () => {
    if (!buildingId) {
      toast.error('Vui lòng chọn chung cư');
      return;
    }
    try {
      await incomeExpenseApi.downloadImportSample(buildingId, importType);
    } catch {
      toast.error('Không thể tải file mẫu');
    }
  };

  const handleImport = async () => {
    if (!buildingId || !importFile) {
      setImportError('Vui lòng chọn chung cư và file Excel');
      return;
    }
    setImporting(true);
    setImportError(null);
    try {
      const result = await importApiMap[importType](buildingId, importFile);
      if (result.errors?.length) {
        toast.error(`Import xong với ${result.errorCount} nhóm lỗi`);
        setImportError(result.errors.join('\n'));
      } else {
        toast.success(`Đã import thành công ${result.successCount} dòng`);
        setShowModal(false);
        setImportFile(null);
      }
      loadBatches();
    } catch (error: any) {
      const errors = error?.response?.data?.errors;
      const message = error?.response?.data?.message || 'Không thể import file';
      setImportError(Array.isArray(errors) ? errors.join('\n') : message);
    } finally {
      setImporting(false);
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-3">
        {shouldShowBuildingSelector(buildings) && (
        <select
          value={buildingId}
          onChange={(e) => selectBuilding(user?.id, e.target.value, setBuildingId)}
                disabled={!needsBuildingSelection(buildings)}
          className="rounded-lg border border-gray-200 px-3 py-2 text-sm"
        >
          {buildings.map((b) => (
            <option key={b.id} value={b.id}>{b.name}</option>
          ))}
        </select>
        )}
        <button
          type="button"
          onClick={() => {
            setImportError(null);
            setImportFile(null);
            setShowModal(true);
          }}
          className="px-4 py-2 rounded-lg bg-orange-500 hover:bg-orange-600 text-white text-sm font-medium"
        >
          + Import file
        </button>
      </div>

      <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
        <table className="min-w-full divide-y divide-gray-200 text-sm">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-4 py-3 text-left font-semibold text-gray-600">Tên file</th>
              <th className="px-4 py-3 text-left font-semibold text-gray-600">Tệp import</th>
              <th className="px-4 py-3 text-left font-semibold text-gray-600">Ngày import</th>
              <th className="px-4 py-3 text-right font-semibold text-gray-600">Số dòng</th>
              <th className="px-4 py-3 text-right font-semibold text-gray-600">Thành công</th>
              <th className="px-4 py-3 text-right font-semibold text-gray-600">Lỗi</th>
              <th className="px-4 py-3 text-left font-semibold text-gray-600">Người import</th>
              <th className="px-4 py-3 text-left font-semibold text-gray-600">Trạng thái</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={8} className="px-4 py-10 text-center text-gray-500">Đang tải...</td></tr>
            ) : batches.length === 0 ? (
              <tr><td colSpan={8} className="px-4 py-10 text-center text-gray-500">Chưa có lịch sử import {title.toLowerCase()}</td></tr>
            ) : (
              batches.map((batch) => (
                <tr key={batch.id} className="border-t">
                  <td className="px-4 py-3">{batch.fileName}</td>
                  <td className="px-4 py-3">
                    <ImportFileLink fileName={batch.fileName} fileUrl={batch.fileUrl} />
                  </td>
                  <td className="px-4 py-3">{new Date(batch.createdAt).toLocaleString('vi-VN')}</td>
                  <td className="px-4 py-3 text-right">{batch.totalRows}</td>
                  <td className="px-4 py-3 text-right text-green-600">{batch.successCount}</td>
                  <td className="px-4 py-3 text-right text-red-600">{batch.errorCount}</td>
                  <td className="px-4 py-3">{batch.creator?.name || '—'}</td>
                  <td className="px-4 py-3">
                    {batch.errorCount > 0 ? (
                      <span className="text-orange-600">Một phần</span>
                    ) : (
                      <span className="text-green-600">Hoàn tất</span>
                    )}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
        <div className="px-4 py-3 border-t bg-gray-50 text-sm text-gray-500">Tổng {batches.length}</div>
      </div>

      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-3xl max-h-[92vh] overflow-y-auto">
            <div className="flex justify-between items-center p-6 border-b bg-gray-50 rounded-t-lg">
              <div>
                <h2 className="text-xl font-bold text-gray-800">Import {title}</h2>
                <p className="text-sm text-gray-500 mt-1">Sheet bắt buộc: Nhập liệu</p>
              </div>
              <button type="button" onClick={() => setShowModal(false)} className="text-gray-400 hover:text-gray-600 p-2">✕</button>
            </div>

            <div className="p-6 space-y-6">
              <div>
                <h3 className="text-lg font-medium text-gray-700 mb-3">Bước 1: Tải file mẫu</h3>
                <p className="text-sm text-gray-600 mb-3">
                  File mẫu gồm sheet <strong>Nhập liệu</strong>, <strong>Hướng dẫn</strong>
                  {importType === 'REVENUE_PLAN' && (
                    <> và <strong>Dữ liệu tham chiếu</strong> (Loại thu, Tài khoản, Mã căn hộ từ dữ liệu hiện tại)</>
                  )}
                  .
                </p>
                <button
                  type="button"
                  onClick={handleDownloadSample}
                  className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded-lg text-sm flex items-center gap-2"
                >
                  Tải sample file
                </button>
              </div>

              <div>
                <h3 className="text-lg font-medium text-gray-700 mb-3">Bước 2: Tải file Excel</h3>
                <div className="border-2 border-dashed border-gray-300 rounded-lg p-4 text-center">
                  <input
                    type="file"
                    accept=".xlsx,.xls"
                    onChange={(e) => setImportFile(e.target.files?.[0] || null)}
                    className="hidden"
                    id={`ie-import-file-${importType}`}
                  />
                  <label htmlFor={`ie-import-file-${importType}`} className="cursor-pointer text-sm text-gray-600">
                    {importFile ? importFile.name : 'Nhấn để chọn file Excel (.xlsx)'}
                  </label>
                </div>
              </div>

              {importError && (
                <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm whitespace-pre-wrap max-h-40 overflow-y-auto">
                  {importError}
                </div>
              )}
            </div>

            <div className="p-6 border-t bg-gray-50 rounded-b-lg flex justify-end gap-3">
              <button type="button" onClick={() => setShowModal(false)} className="px-4 py-2 border rounded-lg text-sm">Hủy</button>
              <button
                type="button"
                onClick={handleImport}
                disabled={importing || !importFile}
                className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg text-sm disabled:opacity-50"
              >
                {importing ? 'Đang import...' : 'Import dữ liệu'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DataImportTab;
